/**
 * EL GASTO, OPERACIÓN A OPERACIÓN (Rais, 23-sep: «el detalle del gasto por operación, para que el
 * escritor sepa dónde concentrarse»).
 *
 * El total que sale abajo a la izquierda dice cuánto llevas; esto dice EN QUÉ. Una pasada de siete
 * operaciones es una línea con su coste —no siete llamadas sueltas—, porque lo que el autor decide
 * es hacer la pasada, no cada llamada. Y los tokens van separados: lo que entra, lo que sale y lo
 * que se leyó de caché, que es de lo que sale la diferencia entre un chat barato y uno caro.
 */
import { useCallback, useEffect, useState } from "react";
import { gasto, dolares, miles } from "../api";
import type { Gasto, GastoLinea } from "../api";
import { nombreDeRuta } from "../logica";

const NOMBRES: Record<string, string> = {
  pasada: "Pasadas", operaciones: "Pasadas por escenas", correccion: "Correcciones", plan: "Planes",
  "informe-global": "Informes de lectura", informe: "Informes", "informe-comparado": "Informes comparados",
  chat: "Chats", "chat-session": "Chats", reescritura: "Chat de reescritura", entregable: "Entregables",
  escenas: "Lectura de escenas", lectura: "Lecturas del documento", estilo: "Estilos", medicion: "Mediciones",
};
const nombreDe = (op: string) => NOMBRES[op] ?? op;

export default function Gastos({ carpeta, nombres }: { carpeta: string; nombres: Record<string, string> }) {
  const [g, setG] = useState<Gasto | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [soloLibro, setSoloLibro] = useState(true);

  const cargar = useCallback(() => {
    gasto(soloLibro ? carpeta : null).then(setG).catch((e) => setFallo((e as Error).message));
  }, [carpeta, soloLibro]);
  useEffect(cargar, [cargar]);

  if (fallo) return <section className="carta"><h2>Gasto</h2><p className="vacio">{fallo}</p></section>;
  if (!g) return <section className="carta"><h2>Gasto</h2><p className="vacio">…</p></section>;

  const filas = Object.entries(g.porOp).sort((a, b) => b[1].coste - a[1].coste);
  const agentes = Object.entries(g.porAgente).sort((a, b) => b[1].coste - a[1].coste);
  const modelos = Object.entries(g.porModelo).sort((a, b) => b[1].coste - a[1].coste);
  const pct = (c: number) => (g.total.coste ? Math.round((c / g.total.coste) * 100) : 0);

  return (
    <>
      <section className="carta">
        <h2>En qué se te va<span className="sub">{soloLibro ? "en este libro" : "en todo lo que has hecho"}</span></h2>
        <div className="botones">
          <button className={"chip" + (soloLibro ? " puesto" : "")} onClick={() => setSoloLibro(true)}>Este libro</button>
          <button className={"chip" + (!soloLibro ? " puesto" : "")} onClick={() => setSoloLibro(false)}>Todo</button>
          <button className="btn texto" onClick={cargar}>Actualizar</button>
        </div>
        <div className="rejilla">
          <div className="cifra"><b>{dolares(g.total.coste)}</b><span>de tu llave</span></div>
          <div className="cifra"><b>{miles(g.total.ops)}</b><span>encargos · {miles(g.total.llamadas)} llamadas</span></div>
          <div className="cifra"><b>{miles(g.total.entrada + g.total.salida)}</b><span>tokens · {miles(g.total.cacheLeida)} leídos de caché</span></div>
        </div>

        <table className="tabla-gasto">
          <thead><tr><th>Qué</th><th>Encargos</th><th>Entrada</th><th>Salida</th><th>Caché</th><th>Coste</th><th /></tr></thead>
          <tbody>
            {filas.map(([op, x]) => (
              <tr key={op}>
                <td><b>{nombreDe(op)}</b></td>
                <td>{miles(x.ops)}</td>
                <td>{miles(x.entrada)}</td>
                <td>{miles(x.salida)}{x.razonamiento ? <span className="vacio"> · {miles(x.razonamiento)} razonando</span> : null}</td>
                <td>{x.cacheLeida ? miles(x.cacheLeida) : "—"}</td>
                <td><b>{dolares(x.coste)}</b></td>
                <td className="parte"><i style={{ width: `${pct(x.coste)}%` }} /></td>
              </tr>
            ))}
            {!filas.length && <tr><td colSpan={7} className="vacio">Todavía no has gastado nada aquí.</td></tr>}
          </tbody>
        </table>
        <p className="vacio">
          Una pasada de siete operaciones es <b>una línea</b>, con lo que costaron sus siete llamadas: lo que decides
          es hacer la pasada. La caché es lo que no se ha vuelto a pagar por repetir el mismo contexto.
        </p>
      </section>

      {modelos.length > 0 && (
        <section className="carta">
          <h2>Por modelo<span className="sub">lo que cuesta cada uno</span></h2>
          <div className="notas">
            {modelos.map(([id, x]) => (
              <div className="nota" key={id}>
                <span className="et">{id.split("/").pop()}</span>
                <b>{dolares(x.coste)}</b>
                <span className="raya"><i style={{ width: `${pct(x.coste)}%` }} /></span>
              </div>
            ))}
          </div>
          <p className="vacio">Si un modelo caro se lleva la mitad del gasto en encargos donde no hace falta, se cambia en Modelos.</p>
        </section>
      )}

      {agentes.length > 0 && (
        <section className="carta">
          <h2>Por colaborador<span className="sub">quién te cuesta</span></h2>
          <div className="notas">
            {agentes.map(([id, x]) => (
              <div className="nota" key={id}>
                <span className="et">{nombres[id] ?? id}</span>
                <b>{dolares(x.coste)}</b>
                <span className="raya"><i style={{ width: `${pct(x.coste)}%`, background: "var(--azul)" }} /></span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="carta">
        <h2>Una a una<span className="sub">lo último primero</span></h2>
        <div className="lista-etapa">
          {g.operaciones.map((o: GastoLinea) => (
            <div className="fila-etapa" key={o.id}>
              <div className="que">
                <b>{o.titulo ? o.titulo.slice(0, 90) : nombreDe(o.op)}</b>
                <span className="vacio">
                  {new Date(o.ts).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {" · "}{nombreDe(o.op)}{o.agente ? ` · ${nombres[o.agente] ?? o.agente}` : ""}
                  {o.llamadas ? ` · ${miles(o.llamadas)} llamadas` : ""}
                  {o.doc ? ` · ${nombreDeRuta(o.doc)}` : ""}
                  {` · ${miles(o.entrada)} de entrada · ${miles(o.salida)} de salida`}
                  {o.razonamiento ? ` (${miles(o.razonamiento)} razonando)` : ""}
                  {o.cacheLeida ? ` · ${miles(o.cacheLeida)} de caché` : ""}
                </span>
              </div>
              <b>{dolares(o.coste)}</b>
            </div>
          ))}
          {!g.operaciones.length && <p className="vacio">Nada todavía.</p>}
        </div>
      </section>
    </>
  );
}
